import React from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Switch,
  Tabs,
  Row,
  Col,
  Statistic,
  Avatar,
  Tooltip,
} from 'antd';
import {
  UserOutlined,
  EditOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  PlusOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  TeamOutlined,
  ShopOutlined,
  CarOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import api from '../services/api';

const { Option } = Select;

const Users = ({ userType }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [isDetailModalVisible, setIsDetailModalVisible] = React.useState(false);
  const [viewingUser, setViewingUser] = React.useState(null);
  const [isLocationModalVisible, setIsLocationModalVisible] = React.useState(false);
  const [form] = Form.useForm();
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || '';
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const perPage = parseInt(searchParams.get('limit')) || 10;
  const queryClient = useQueryClient();

  const setSearchParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
  };

  // Fetch users with filters
  const { data: usersResponse = { data: [], total: 0 }, isLoading } = useQuery(
    ['users', userType, searchTerm, statusFilter, currentPage, perPage],
    async () => {
      try {
        const params = new URLSearchParams();
        if (searchTerm) params.append('search', searchTerm);
        if (statusFilter) params.append('status', statusFilter);
        if (userType) params.append('user_type', userType);
        params.append('page', currentPage);
        params.append('per_page', perPage);

        const response = await api.get(`/admin/users?${params.toString()}`);
        return response.data?.data || response.data || [];
      } catch (error) {
        console.error('Failed to fetch users:', error);
        if (error.response?.status === 401) {
          window.location.href = '/login';
        }
        throw error;
      }
    },
    {
      retry: (failureCount, error) => {
        if (error.response?.status === 401) return false;
        if (error.response?.status === 403) return false;
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  const users = Array.isArray(usersResponse) ? usersResponse : usersResponse.data || [];
  const total = usersResponse.total || 0;

  // Fetch user orders (for detail view)
  const { data: userOrders = [], isLoading: ordersLoading } = useQuery(
    ['user-orders', viewingUser?.id],
    async () => {
      if (!viewingUser?.id) return [];
      const response = await api.get(`/admin/users/${viewingUser.id}/orders`);
      return response.data?.data || response.data || [];
    },
    { enabled: !!viewingUser?.id },
  );

  // Fetch user wallet (for detail view)
  const { data: userWallet = null } = useQuery(
    ['user-wallet', viewingUser?.id],
    async () => {
      if (!viewingUser?.id) return null;
      const response = await api.get(`/admin/users/${viewingUser.id}/wallet`);
      return response.data?.data || response.data || null;
    },
    { enabled: !!viewingUser?.id },
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    async ({ id, ...values }) => {
      const response = await api.put(`/admin/users/${id}`, values);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User updated successfully');
        queryClient.invalidateQueries('users');
        setIsModalVisible(false);
        setEditingUser(null);
        form.resetFields();
      },
      onError: (_error) => {
        message.error('Failed to update user');
      },
    },
  );

  // Approve user mutation
  const approveUserMutation = useMutation(
    async (userId) => {
      const response = await api.post(`/admin/users/${userId}/approve`);
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User approved successfully');
        queryClient.invalidateQueries('users');
      },
      onError: (_error) => {
        message.error('Failed to approve user');
      },
    },
  );

  // Toggle user status mutation
  const toggleUserStatusMutation = useMutation(
    async ({ userId, currentStatus }) => {
      const newStatus = currentStatus ? 'inactive' : 'active';
      const response = await api.patch(`/admin/users/${userId}/toggle-status`, {
        status: newStatus,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        message.success('User status updated successfully');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        message.error(
          'Failed to update user status: ' + (error.response?.data?.message || error.message),
        );
      },
    },
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    async (id) => {
      await api.delete(`/admin/users/${id}`);
    },
    {
      onSuccess: () => {
        message.success('User deleted successfully');
        queryClient.invalidateQueries(['users', userType]);
      },
      onError: (_error) => {
        message.error('Failed to delete user');
      },
    },
  );

  const handleEdit = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      ...record,
      is_active: record.is_active,
    });
    setIsModalVisible(true);
  };

  const handleView = (record) => {
    setViewingUser(record);
    setIsDetailModalVisible(true);
  };

  const handleApprove = (record) => {
    approveUserMutation.mutate(record.id);
  };

  const handleToggleStatus = (record) => {
    toggleUserStatusMutation.mutate({ userId: record.id, currentStatus: record.is_active });
  };

  const handleLocation = (record) => {
    setViewingUser(record);
    setIsLocationModalVisible(true);
  };

  const handleSubmit = (values) => {
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, ...values });
    }
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this user?',
      content: `User: ${record.name || record.full_name}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: () => {
        deleteUserMutation.mutate(record.id);
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Avatar size="small" icon={<UserOutlined />} />
          <span>{record.name || record.full_name || 'N/A'}</span>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => (
        <Space>
          <MailOutlined />
          <span>{email}</span>
        </Space>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || 'N/A',
    },
    {
      title: 'Type',
      dataIndex: 'user_type',
      key: 'user_type',
      render: (type) => {
        const config = {
          customer: { icon: <UserOutlined />, color: 'green', label: 'Customer' },
          provider: { icon: <ShopOutlined />, color: 'blue', label: 'Provider' },
          delivery: { icon: <CarOutlined />, color: 'orange', label: 'Delivery' },
          admin: { icon: <TeamOutlined />, color: 'red', label: 'Admin' },
        };
        const { icon, color, label } = config[type] || {
          icon: <UserOutlined />,
          color: 'default',
          label: type,
        };
        return (
          <Tag color={color} icon={icon}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Space>
          <Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Active' : 'Inactive'}</Tag>
          {!record.is_verified && (
            <Tag color="orange" icon={<ExclamationCircleOutlined />}>
              Unverified
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Membership',
      key: 'membership',
      render: (_, record) => {
        if (record.user_type !== 'customer') return <Tag>N/A</Tag>;
        if (record.has_membership) {
          const m = record.membership || {};
          const daysLeft = m.days_remaining ?? 0;
          return (
            <Tooltip
              title={
                <div>
                  {m.membership_start_date && (
                    <div>Start: {new Date(m.membership_start_date).toLocaleDateString()}</div>
                  )}
                  {m.membership_end_date && (
                    <div>End: {new Date(m.membership_end_date).toLocaleDateString()}</div>
                  )}
                  <div>Days remaining: {daysLeft}</div>
                </div>
              }
            >
              <Tag color="green" icon={<CrownOutlined />}>
                Active {daysLeft > 0 && <span>({daysLeft}d)</span>}
              </Tag>
            </Tooltip>
          );
        }
        return <Tag color="default">None</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button type="text" icon={<UserOutlined />} onClick={() => handleView(record)} />
          </Tooltip>
          {userType === 'customer' && (
            <Tooltip title="Customer 360 Profile">
              <Button
                type="text"
                icon={<ShopOutlined />}
                onClick={() => navigate(`/customers/${record.id}`)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Tooltip title="Toggle Status">
            <Button
              type="text"
              icon={record.is_active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleStatus(record)}
            />
          </Tooltip>
          {!record.is_verified && (
            <Tooltip title="Approve User">
              <Button
                type="text"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(record)}
              />
            </Tooltip>
          )}
          {(record.user_type === 'provider' || record.user_type === 'delivery') && (
            <Tooltip title="Location">
              <Button
                type="text"
                icon={<EnvironmentOutlined />}
                onClick={() => handleLocation(record)}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={`${userType?.charAt(0).toUpperCase() + userType?.slice(1)} Management`}
        extra={
          <Space>
            <Input.Search
              placeholder="Search users..."
              allowClear
              style={{ width: 250 }}
              value={searchTerm}
              onChange={(e) => setSearchParam('search', e.target.value)}
              onSearch={(value) => setSearchParam('search', value)}
            />
            <Select
              placeholder="Filter by status"
              allowClear
              style={{ width: 150 }}
              value={statusFilter || undefined}
              onChange={(value) => setSearchParam('status', value)}
              options={[
                { label: 'Active', value: 'active' },
                { label: 'Inactive', value: 'inactive' },
              ]}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingUser(null);
                form.resetFields();
                setIsModalVisible(true);
              }}
            >
              Add User
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: currentPage,
            pageSize: perPage,
            total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            pageSizeOptions: ['5', '10', '20', '50'],
            onChange: (page, pageSize) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', page);
              params.set('limit', pageSize);
              params.delete('offset');
              setSearchParams(params, { replace: true });
            },
            onShowSizeChange: (_, size) => {
              const params = new URLSearchParams(searchParams);
              params.set('page', 1);
              params.set('limit', size);
              params.delete('offset');
              setSearchParams(params, { replace: true });
            },
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Edit User Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsModalVisible(false);
              setEditingUser(null);
              form.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={() => form.submit()}>
            {editingUser ? 'Update' : 'Create'}
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Full Name"
                name="name"
                rules={[{ required: true, message: 'Please enter full name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Email"
                name="email"
                rules={[{ required: true, message: 'Please enter email' }]}
              >
                <Input type="email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Phone"
                name="phone"
                rules={[{ pattern: /^\+?\d+$/, message: 'Please enter numbers and + only' }]}
              >
                <Input
                  type="tel"
                  onKeyPress={(e) => e.key !== '+' && !/[0-9]/.test(e.key) && e.preventDefault()}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="User Type"
                name="user_type"
                rules={[{ required: true, message: 'Please select user type' }]}
              >
                <Select>
                  <Option value="admin">Admin</Option>
                  <Option value="customer">Customer</Option>
                  <Option value="provider">Provider</Option>
                  <Option value="delivery">Delivery</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Status" name="is_active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Verified" name="is_verified" valuePropName="checked">
                <Switch checkedChildren="Verified" unCheckedChildren="Unverified" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* User Details Modal */}
      <Modal
        title="User Details"
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setViewingUser(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setViewingUser(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={1000}
      >
        {viewingUser && (
          <Tabs defaultActiveKey="basic">
            <Tabs.TabPane tab="Basic Information" key="basic">
              <Row gutter={16}>
                <Col span={12}>
                  <Card size="small">
                    <h4>Personal Information</h4>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <strong>Name:</strong> {viewingUser.name || viewingUser.full_name}
                      </div>
                      <div>
                        <strong>Email:</strong> {viewingUser.email}
                      </div>
                      <div>
                        <strong>Phone:</strong> {viewingUser.phone || 'N/A'}
                      </div>
                      <div>
                        <strong>User Type:</strong> {viewingUser.user_type?.toUpperCase()}
                      </div>
                      <div>
                        <strong>Status:</strong> {viewingUser.is_active ? 'Active' : 'Inactive'}
                      </div>
                      <div>
                        <strong>Verified:</strong> {viewingUser.is_verified ? 'Yes' : 'No'}
                      </div>
                      <div>
                        <strong>Joined:</strong>{' '}
                        {new Date(viewingUser.created_at).toLocaleDateString()}
                      </div>
                      <div>
                        <strong>Referral Code:</strong> {viewingUser.referral_code || 'N/A'}
                      </div>
                    </Space>
                  </Card>
                </Col>
                <Col span={12}>
                  <Card size="small">
                    <h4>Account Status</h4>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <div>
                        <strong>Account Status:</strong>
                        <Tag color={viewingUser.is_active ? 'green' : 'red'}>
                          {viewingUser.is_active ? 'Active' : 'Inactive'}
                        </Tag>
                      </div>
                      <div>
                        <strong>Verification Status:</strong>
                        <Tag color={viewingUser.is_verified ? 'green' : 'orange'}>
                          {viewingUser.is_verified ? 'Verified' : 'Unverified'}
                        </Tag>
                      </div>
                      <div>
                        <strong>Last Login:</strong>{' '}
                        {viewingUser.last_login_at
                          ? new Date(viewingUser.last_login_at).toLocaleString()
                          : 'Never'}
                      </div>
                      <div>
                        <strong>IP Address:</strong> {viewingUser.last_ip || 'N/A'}
                      </div>
                    </Space>
                  </Card>
                </Col>
              </Row>
            </Tabs.TabPane>

            <Tabs.TabPane tab="Orders" key="orders">
              <Table
                columns={[
                  { title: 'Order ID', dataIndex: 'order_number', key: 'order_number' },
                  {
                    title: 'Amount',
                    dataIndex: 'total_amount',
                    key: 'total_amount',
                    render: (amount) => `Rs. ${parseFloat(amount || 0).toFixed(2)}`,
                  },
                  {
                    title: 'Status',
                    dataIndex: 'order_status',
                    key: 'order_status',
                    render: (status) => <Tag>{status?.replace('_', ' ').toUpperCase()}</Tag>,
                  },
                  {
                    title: 'Date',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: (date) => new Date(date).toLocaleDateString(),
                  },
                ]}
                dataSource={userOrders}
                loading={ordersLoading}
                pagination={{
                  pageSize: 5,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                }}
                size="small"
              />
            </Tabs.TabPane>

            <Tabs.TabPane tab="Wallet" key="wallet">
              <Card size="small">
                <Row gutter={16}>
                  <Col span={8}>
                    <Statistic
                      title="Current Balance"
                      value={userWallet?.balance || 0}
                      precision={2}
                      prefix="Rs. "
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Total Transactions"
                      value={userWallet?.transactions?.length || 0}
                    />
                  </Col>
                  <Col span={8}>
                    <Statistic
                      title="Last Transaction"
                      value={
                        userWallet?.last_transaction
                          ? new Date(userWallet.last_transaction).toLocaleDateString()
                          : 'No transactions'
                      }
                    />
                  </Col>
                </Row>
              </Card>
            </Tabs.TabPane>
          </Tabs>
        )}
      </Modal>

      {/* Location Modal */}
      <Modal
        title="User Location"
        open={isLocationModalVisible}
        onCancel={() => {
          setIsLocationModalVisible(false);
          setViewingUser(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsLocationModalVisible(false);
              setViewingUser(null);
            }}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {viewingUser && (
          <div style={{ padding: '20px 0' }}>
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <h4>Current Location</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Latitude:</strong> {viewingUser.latitude || 'N/A'}
                    </div>
                    <div>
                      <strong>Longitude:</strong> {viewingUser.longitude || 'N/A'}
                    </div>
                    <div>
                      <strong>Address:</strong> {viewingUser.current_location || 'N/A'}
                    </div>
                    <div>
                      <strong>Last Updated:</strong>{' '}
                      {viewingUser.location_updated_at
                        ? new Date(viewingUser.location_updated_at).toLocaleString()
                        : 'Never'}
                    </div>
                  </Space>
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <h4>Tracking Status</h4>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <strong>Status:</strong> {viewingUser.tracking_status || 'Offline'}
                    </div>
                    <div>
                      <strong>Activity:</strong> {viewingUser.activity_status || 'Inactive'}
                    </div>
                    {viewingUser.user_type === 'provider' && (
                      <>
                        <div>
                          <strong>Available:</strong> {viewingUser.is_available ? 'Yes' : 'No'}
                        </div>
                        <div>
                          <strong>Service Area:</strong> {viewingUser.service_area || 'N/A'}
                        </div>
                      </>
                    )}
                    {viewingUser.user_type === 'delivery' && (
                      <>
                        <div>
                          <strong>Vehicle Type:</strong> {viewingUser.vehicle_type || 'N/A'}
                        </div>
                        <div>
                          <strong>Vehicle Number:</strong> {viewingUser.vehicle_number || 'N/A'}
                        </div>
                      </>
                    )}
                  </Space>
                </Card>
              </Col>
            </Row>

            {viewingUser.latitude && viewingUser.longitude && (
              <div style={{ marginTop: 16, height: 300, borderRadius: 8, overflow: 'hidden' }}>
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ border: 0, borderRadius: 8 }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(viewingUser.longitude) - 0.01}%2C${parseFloat(viewingUser.latitude) - 0.01}%2C${parseFloat(viewingUser.longitude) + 0.01}%2C${parseFloat(viewingUser.latitude) + 0.01}&layer=mapnik&marker=${viewingUser.latitude}%2C${viewingUser.longitude}`}
                  title="User Location"
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Users;
